/* Gift options on the product page (4b). Impulse 7.1 (Spiritly).
   - Nothing ticked: the theme's own Add to Basket runs untouched.
   - Box and/or card ticked: add the bottle, then (only if that worked) its boxes with 'Gift box for'/_for like the
     drawer editor, then fire the theme's "ajaxProduct:added" so it rebuilds and opens the drawer, then open
     the card editor from gift-upsell.js by clicking the drawer's own "Add a greeting card" opener (a click
     outside the drawer would make the theme close it).
   - Bottle first, boxes second: Shopify can part-add a request that runs out of stock, and the drawer's
     housekeeping (sgSync) can act on that half-added cart. Adding boxes only after the bottle succeeds means
     a stock error never puts box lines in the cart. Any failure puts the cart back exactly as it was.
   The theme adds on the button click as well as on submit, so both are intercepted (window, capture phase). */
(() => {
  if (window.__goLoaded) return;
  window.__goLoaded = true;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const money = c => {
    try { if (window.theme && theme.Currency && theme.Currency.formatMoney) return theme.Currency.formatMoney(c, theme.settings.moneyFormat); } catch (e) {}
    return '£' + (c / 100).toFixed(2);
  };
  const api = (path, body) => fetch(root + path, body ? {
    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
  } : { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => {
    // The cookie-banner script can hold cart updates back and return a bare { ok: false } stand-in.
    if (typeof r.json !== 'function') throw { description: 'Please respond to the cookie banner first, then try again.' };
    return r.json().then(d => { if (!r.ok) throw d; return d; });
  });
  const isCard = i => !!i.handle && i.handle.indexOf('spiritly-greeting-card-') === 0;
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const forms = new Map();

  // Put every line back to the quantity it had in `before` (new lines go to 0).
  function restore(before) {
    const prev = {};
    before.items.forEach(i => (prev[i.key] = i.quantity));
    return api('cart.js').then(now => {
      const upd = {};
      now.items.forEach(i => { const q = prev[i.key] || 0; if (q !== i.quantity) upd[i.key] = q; });
      return Object.keys(upd).length ? api('cart/update.js', { updates: upd }).then(() => true) : false;
    });
  }
  // Restore, then check again once the drawer's housekeeping has settled.
  function rollback(before) {
    if (!before) return Promise.resolve();
    return restore(before).then(() => wait(900)).then(() => restore(before)).catch(() => {})
      .then(() => (window.sgSync ? window.sgSync() : null)).catch(() => {});
  }
  const friendly = err => {
    const d = (err && (err.description || err.message)) || '';
    if (/were added|enough|stock|available|sold out/i.test(d)) return 'Sorry, we don’t have enough of this in stock to add that many. Nothing was added to your basket.';
    return d || 'Sorry, something went wrong. Please try again.';
  };

  function init(go) {
    if (go.dataset.ready) return;
    const form = go.closest('form');
    if (!form) return;
    go.dataset.ready = '1';
    const formId = form.getAttribute('id');
    const prices = JSON.parse(go.querySelector('[data-go-prices]').textContent);
    const boxPrice = +go.dataset.boxPrice, cardPrice = +go.dataset.cardPrice;
    const box = go.querySelector('[data-go-box]'), card = go.querySelector('[data-go-card]');
    const cardRow = go.querySelector('[data-go-card-row]'), cardDone = go.querySelector('[data-go-card-done]');
    const btns = () => [...form.querySelectorAll('button[type="submit"][name="add"]')];
    const qtyInput = () => form.querySelector('[name="quantity"]') || (formId && document.querySelector(`[name="quantity"][form="${formId}"]`));
    const qty = () => Math.max(1, parseInt((qtyInput() || {}).value, 10) || 1);
    const variant = () => String((form.querySelector('[name="id"]') || {}).value || '');
    const boxOn = () => !!(box && box.checked);
    const cardOn = () => !!(card && card.checked && !cardRow.hidden);
    let busy = false;
    // Browsers can restore ticked boxes when the page is reloaded; always start unticked.
    if (box) box.checked = false;
    if (card) card.checked = false;

    function paint() {
      const q = qty();
      go.querySelectorAll('label.go__row').forEach(r => r.classList.toggle('is-on', r.querySelector('input').checked));
      if (box) {
        // One box per bottle: at qty 2+ say so in the subline and show the price per box, not one lump sum.
        go.querySelector('[data-go-box-sub]').textContent = q > 1 ? `Boxed and ribboned · one box per bottle (${q} boxes)` : 'Boxed and ribboned';
        go.querySelector('[data-go-box-price]').textContent = q > 1 ? `+ ${money(boxPrice)} each` : '+ ' + money(boxPrice);
      }
      const extra = boxOn() || cardOn();
      const total = (prices[variant()] || 0) * q + (boxOn() ? boxPrice * q : 0) + (cardOn() ? cardPrice : 0);
      btns().forEach(b => {
        const l = b.querySelector('[data-add-to-cart-text]');
        if (!l || b.disabled) return; // leave the theme's Sold out / loading labels alone
        const base = l.dataset.defaultText || l.textContent.trim();
        const txt = extra ? `${base} · ${money(total)}` : base;
        if (l.textContent.trim() !== txt) l.textContent = txt;
      });
    }

    function setCardInCart(has) {
      if (!cardRow) return;
      cardRow.hidden = has; cardDone.hidden = !has;
      if (has && card) card.checked = false;
      paint();
    }

    function showError(msg) {
      form.querySelectorAll('.errors').forEach(x => x.remove());
      if (!msg) return;
      const d = document.createElement('div');
      d.className = 'errors text-center';
      d.setAttribute('role', 'alert');
      d.textContent = msg;
      form.appendChild(d);
    }

    function openCardEditor() {
      const inDrawer = [...document.querySelectorAll('#CartDrawer [data-sg-open="card"]')].find(x => x.offsetParent);
      const o = inDrawer || go.querySelector('[data-go-opener]');
      if (o) o.click();
    }

    function submit(btn) {
      if (busy) return;
      busy = true;
      const q = qty(), id = variant(), boxed = boxOn(), carded = cardOn();
      const bottle = { id: +id, quantity: q };
      const boxes = { id: +go.dataset.boxVariant, quantity: q, properties: { 'Gift box for': go.dataset.title, _for: id } };
      const all = btns();
      all.forEach(b => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      if (btn) btn.classList.add('btn--loading');
      showError('');
      let before = null;
      api('cart.js')
        .then(c => { before = c; return api('cart/add.js', { items: [bottle] }); })
        // Card only: no boxes to add, so carry on with a resolved promise (not null).
        .then(data => (boxed ? api('cart/add.js', { items: [boxes] }) : Promise.resolve()).then(() => data))
        .then(data => {
          if (box) box.checked = false;
          if (card) card.checked = false;
          // The theme rebuilds the drawer, opens it and then fires cart:updated.
          const built = new Promise(res => {
            const h = () => { document.removeEventListener('cart:updated', h); res(); };
            document.addEventListener('cart:updated', h);
            setTimeout(res, 3000);
          });
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: btn } }));
          if (carded) built.then(() => setTimeout(openCardEditor, 400));
        })
        .catch(err => rollback(before).then(() => {
          document.dispatchEvent(new CustomEvent('cart:build'));
          showError(friendly(err));
        }))
        .finally(() => {
          busy = false;
          all.forEach(b => { b.disabled = false; b.removeAttribute('aria-busy'); });
          if (btn) btn.classList.remove('btn--loading');
          paint();
        });
    }

    forms.set(form, { paint, setCardInCart, submit, ticked: () => boxOn() || cardOn() });
    paint();
  }

  const paintAll = () => requestAnimationFrame(() => forms.forEach(f => f.paint()));
  const scan = () => document.querySelectorAll('[data-go]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', scan) : scan();
  // Back/forward cache: reset ticks when the page is shown again.
  window.addEventListener('pageshow', e => { if (e.persisted) document.querySelectorAll('[data-go] input[type="checkbox"]').forEach(i => (i.checked = false)); paintAll(); });

  // Quantity +/- buttons, typing, variant picks and tick changes all repaint the rows and the button total.
  ['click', 'input', 'change', 'keyup'].forEach(ev => document.addEventListener(ev, paintAll));
  document.addEventListener('variantChange', paintAll);

  // Intercept before the theme's own add-to-cart (it listens to the button click and the form submit).
  window.addEventListener('click', e => {
    const b = e.target && e.target.closest && e.target.closest('button[type="submit"][name="add"]');
    const f = b && b.form && forms.get(b.form);
    if (!f || !f.ticked()) return; // nothing ticked: normal theme behaviour
    e.preventDefault(); e.stopImmediatePropagation();
    if (!b.disabled) f.submit(b);
  }, true);
  window.addEventListener('submit', e => {
    const f = forms.get(e.target);
    if (!f || !f.ticked()) return;
    e.preventDefault(); e.stopImmediatePropagation();
    f.submit(e.submitter || e.target.querySelector('button[type="submit"][name="add"]'));
  }, true);

  // Keep "Greeting card already in your basket" in step with the cart.
  let t;
  document.addEventListener('cart:updated', e => {
    clearTimeout(t);
    t = setTimeout(() => {
      const c = e.detail && e.detail.cart;
      (c ? Promise.resolve(c) : api('cart.js'))
        .then(cart => { const has = cart.items.some(isCard); forms.forEach(f => f.setCardInCart(has)); })
        .catch(() => {});
    }, 300);
  });
})();
